import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { toast } from 'react-toastify';
import { FiChevronRight, FiChevronDown, FiMessageSquare } from 'react-icons/fi';

import api from 'services/api';
import { useModal } from 'hooks/modal';
import AppError from 'utils/AppError';
import { show } from 'store/modules/dialog/actions';
import { isEleicoesMunicipais } from 'utils/eleicoes';

import {
  ComercialItem,
  InsercaoItem,
  HistoricoItem,
} from 'pages/Eleicoes/@types';

import { Column, Container, DesvincularModal, Row } from './styles';
import DropDownBubble from 'components/DropDownBubble';
import Tooltip from 'components/Tooltip';
import AsyncSelect from 'components/AsyncSelect/withoutForm';
import DropDownMenu from 'components/DropDownMenu';
import Button from 'components/Button';

interface ComercialOption extends ComercialItem {
  value: number;
  label: string;
}
interface PlanoMidiaProps {
  clienteId?: number;
  blocoFilter?: number;
  turnoFilter?: string;
  isBloco: boolean;
  cargo?: string;
  statusFilter?: string;
}
export default function PlanoMidia({
  clienteId,
  blocoFilter,
  turnoFilter,
  isBloco,
  cargo,
  statusFilter,
}: PlanoMidiaProps) {
  /**
   * USE STATES
   */
  const [insercoes, setInsercoes] = useState<InsercaoItem[]>([]);
  const [selected, setSelected] = useState({} as InsercaoItem);
  const [selectedHistorico, setSelectedHistorico] = useState(0);
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);

  // theme color
  const themeContext = useContext(ThemeContext);
  /**
   * CONTEXT API
   */
  const { showModal, hideModal, updateModal, modalData } = useModal();

  /**
   * USE REDUX
   */
  const dispatch = useDispatch();
  const { currentDate, emissora } = useSelector((state: any) => state.usuario);
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  /**
   * USE CALLBACK
   */
  const updateInsercao = useCallback(
    (insercao_id: number, key: string, value: any) => {
      setInsercoes(prev =>
        prev.map(i =>
          i.id === insercao_id
            ? {
                ...i,
                [key]: value,
                // Caso seja alterado a duração. Torna o disableSelect true
                // Isso desabilita a seleção de materiais (que estará populado com os materiais com a duração anterior)
                // Isso evita que seja selecionado material de 30s em inserção de 60s e vice versa
                // Ao atualizar a página, o select será repopulado com os materiais com a duração correta
                ...(key === 'duracao' && { disableSelect: true }),
              }
            : i,
        ),
      );
    },
    [],
  );

  const loadInsercoes = useCallback(() => {
    setLoadingTable(true);
    setSelectedHistorico(0);
    api
      .get('/eleicoes/insercao', {
        params: {
          emissora_id: emissora.value,
          tipo: isBloco ? 'bloco' : 'insercao',
          cliente_id: clienteId,
          data: format(new Date(currentDate), 'yyyy-M-d').toString(),
          ...(cargo && { cargo }),
          ...(statusFilter && { status: statusFilter }),
        },
      })
      .then(response => {
        setInsercoes(
          blocoFilter
            ? response.data.filter((d: InsercaoItem) => d.bloco === blocoFilter)
            : turnoFilter
            ? response.data.filter((d: InsercaoItem) => d.turno === turnoFilter)
            : (response.data as InsercaoItem[]),
        );
      })
      .catch(error => console.log(error))
      .finally(() => setLoadingTable(false));
  }, [
    blocoFilter,
    cargo,
    clienteId,
    currentDate,
    emissora.value,
    isBloco,
    statusFilter,
    turnoFilter,
  ]);

  const handleHistoricoVinculacao = useCallback(
    async (insercao: InsercaoItem) => {
      setSelectedHistorico(insercao.id);
      setLoadingHistorico(true);
      try {
        const response = await api.get(
          `/eleicoes/insercao/${insercao.id}/historico`,
        );
        setHistorico(response.data);
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingHistorico(false);
    },
    [],
  );

  const handleUpdateDuracao = useCallback(
    (insercao: InsercaoItem, duracao: number) => {
      showModal({
        title: 'ATENÇÃO',
        component: (
          <p>
            Alterar duração de <b>{insercao.cliente.mnemonico}</b>?
          </p>
        ),
        callback: async () => {
          updateModal({ ...modalData, loading: true });
          await api
            .put(`/eleicoes/insercao/${insercao.id}`, { duracao })
            .then(() => {
              updateInsercao(insercao.id, 'duracao', duracao);
              toast.success(
                'Duração alterada. Atualize a página para carregar os materiais da nova duração',
              );
              hideModal();
            })
            .catch(error => {
              if (error instanceof Error) toast.error(error.message);
              else {
                const appError = error as AppError;
                toast.error(appError.message);
              }
            });
        },
      });
    },
    [hideModal, modalData, showModal, updateInsercao, updateModal],
  );

  const handleUpdateStatusMapaMidia = useCallback(
    async (insercao: InsercaoItem, newStatusMapa: string) => {
      try {
        setSelectedHistorico(0);
        await api
          .put(`eleicoes/insercao/${insercao.id}`, {
            statusmapa: newStatusMapa,
          })
          .then(response => {
            setInsercoes(
              insercoes.map(i => {
                return i.id === insercao.id ? { ...response.data } : i;
              }),
            );
          });
        toast.success('Status Mapa alterado');
        loadInsercoes();
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    },
    [insercoes, loadInsercoes],
  );

  const copyToClipboard = useCallback(text => {
    const dummy = document.createElement('textarea');
    // to avoid breaking orgain page when copying more words
    // cant copy when adding below this code
    // dummy.style.display = 'none'
    document.body.appendChild(dummy);
    // Be careful if you use texarea. setAttribute('value', value), which works with "input" does not work with "textarea". – Eduard
    dummy.value = text;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
    toast.success('CM copiado para área de transferência');
  }, []);

  const handleUpdateStatusMaterial = useCallback(
    async (insercao: InsercaoItem, newStatusMaterial: string) => {
      try {
        if (!insercao.comercial && newStatusMaterial !== 'slide') {
          return toast.error('Esta inserção não possui comercial vinculado');
        }
        setSelectedHistorico(0);
        await api
          .put(`eleicoes/insercao/${insercao.id}`, {
            statusmaterial: newStatusMaterial,
          })
          .then(response => {
            setInsercoes(prevState =>
              prevState.map(i => {
                return i.id === insercao.id ? { ...response.data } : i;
              }),
            );
          });
        toast.success('Status material alterado');
        loadInsercoes();
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    },
    [loadInsercoes],
  );

  const loadComerciais = useCallback(
    async (value: string, insercao: InsercaoItem) => {
      try {
        const response = await api.get<ComercialItem[]>(
          `/comerciaists/eleicoes`,
          {
            params: {
              cliente_id: insercao.cliente.id,
              cargo: insercao.cargo,
              duracao: insercao.duracao,
              ...(value && { search: value }),
              ...(isEleicoesMunicipais ? { emissora_id: emissora.value } : {}),
              limit: 10,
            },
          },
        );
        return response.data.map(comercial => ({
          value: comercial.id,
          label: `${comercial.codmaterial} - ${comercial.titulo}`,
          ...comercial,
        }));
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    },
    [emissora.value],
  );

  const handleObservacao = useCallback(
    (observacao: string) => {
      const contentHTML = `<p>Observação: ${observacao}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        showConfirmButton: false,
        cancelButtonText: 'FECHAR',
      };
      dispatch(show(options));
    },
    [dispatch],
  );
  const handleSubmitRemove = useCallback(
    async (insercoesSelecionadas: InsercaoItem[]) => {
      const response = await api.patch('/eleicoes/insercao/desvincular', {
        ids: insercoesSelecionadas.map(i => i.id),
      });
      hideModal();

      if (response?.data?.message === 'success') {
        toast.success(
          insercoesSelecionadas.length === 1
            ? 'Material desvinculado com sucesso'
            : `Material desvinculado de ${insercoesSelecionadas.length} inserções`,
        );
        // Fecha linhas de histórico
        setSelectedHistorico(0);
        // Desvincula materiais no painel
        setInsercoes(prevState =>
          prevState.map(i =>
            insercoesSelecionadas.find(s => s.id === i.id)
              ? { ...i, comercial: undefined }
              : i,
          ),
        );
      } else toast.error('Falha ao desvincular material');
    },
    [hideModal],
  );

  const handleRemoveMaterial = useCallback(
    (insercao: InsercaoItem) => {
      if (insercao.comercial) {
        setSelected(insercao);
        showModal({
          title: 'ATENÇÃO',
          component: (
            <DesvincularModal>
              <p>
                Deseja desvincular o material somente desta inserção ou de todas
                desta data?
              </p>
              <div>
                <Button onClick={() => handleSubmitRemove([insercao])}>
                  SOMENTE ESTA INSERÇÃO
                </Button>
                <Button
                  onClick={() =>
                    handleSubmitRemove(
                      insercoes.filter(
                        i => i.comercial?.id === insercao.comercial?.id,
                      ),
                    )
                  }
                >
                  TODAS
                </Button>
              </div>
            </DesvincularModal>
          ),
        });
      }
    },
    [handleSubmitRemove, insercoes, showModal],
  );
  const handleSelectMaterial = useCallback(
    async (comercial: ComercialOption, insercao: InsercaoItem) => {
      if (!comercial?.titulo) {
        toast.error('Erro ao localizar o titulo do material');
        return;
      }
      await api
        .put(`/eleicoes/insercao/${insercao.id}`, {
          comercial_id: comercial.id,
        })
        .then(() => {
          updateInsercao(insercao.id, 'comercial', comercial);
        });

      // 08-02-2023: Comentado pois a OPEC pediu para retirar a confirmação de vinculação de comercial
      // const optionBase = {
      //   type: 'question',
      //   text: `Associar o comercial ${comercial.titulo} ?`,
      //   confirmButtonText: 'CONFIRMAR',
      //   callback: {
      //     url: `/eleicoes/insercao/${insercao.id}`,
      //     method: api.put,
      //     action: 'vinculacao',
      //     data: { comercial_id: comercial.id },
      //   },
      //   haveCallback: true,
      //   successMessage: 'Comercial associado!',
      //   cancelButtonText: 'NÃO',
      //   showCancelButton: false,
      //   showConfirmButton: true,
      //   showCloseButton: true,
      // };
      // dispatch(show(optionBase));
    },
    [updateInsercao],
  );

  const handleActionSelect = useCallback(
    async (action: number, insercao: InsercaoItem) => {
      switch (action) {
        case 1:
          handleRemoveMaterial(insercao);
          break;
        case 2:
          handleUpdateDuracao(insercao, 0);
          break;
        case 3:
          handleUpdateDuracao(insercao, 30);
          break;
        case 4:
          handleUpdateDuracao(insercao, 60);
          break;
        case 5:
          handleUpdateStatusMaterial(insercao, 'slide');
          break;
      }
    },
    [handleRemoveMaterial, handleUpdateDuracao, handleUpdateStatusMaterial],
  );
  const formatRecebidoAt = useCallback((comercial?: ComercialItem) => {
    if (!comercial || !comercial.midia || !comercial.midia.provided_at) {
      return '...';
    }

    return format(parseISO(comercial.midia.provided_at), 'dd/MM/yyyy HH:mm');
  }, []);

  const renderTableInsercao = useCallback(() => {
    const options = [
      {
        color: '#f5365c',
        label: 'Slide',
        value: 'slide',
        show: false,
      },
      {
        color: '#f5365c',
        label: 'Indisponível',
        value: 'indisponivel',
        show: false,
      },
      {
        color: '#56CA56',
        label: 'receber',
        value: 'recebido',
        show: true,
      },
      {
        color: '#000000',
        label: 'autorizar',
        value: 'autorizado',
        show: !isBloco,
      },
      {
        color: '#0055FF',
        label: 'recusar',
        value: 'recusado',
        show: !isBloco,
      },
      {
        color: '#FFC107',
        label: 'atrasar',
        value: 'atrasado',
        show: false,
      },
      {
        color: '#ba54f5',
        label: 'alterar',
        value: 'alterado',
        show: !isBloco,
        disabled: true,
      },
    ];
    return (
      <table id="planomidia_tbody">
        <thead>
          <tr>
            <th style={{ width: '60px' }} />
            {isBloco ? (
              <th style={{ width: '100px' }}>TURNO/SQ</th>
            ) : (
              <th style={{ width: '100px' }}>BL/SQ</th>
            )}
            <th>PARTIDO/CARGO</th>
            <th style={{ width: '120px' }}>DURAÇÃO</th>
            {isBloco && <th style={{ width: '120px' }}>INÍCIO/FIM</th>}
            <th style={{ width: '220px' }}>DEADLINES</th>
            <th style={{ width: '220px' }}>MAPA</th>
            <th>MATERIAL</th>
            {isBloco && <th style={{ width: '60px' }}>OBS</th>}
            <th style={{ width: '60px' }} />
          </tr>
        </thead>

        <tbody>
          {!!insercoes &&
            insercoes.map((insercao: InsercaoItem, index: number) => (
              <React.Fragment key={`frag${index}`}>
                <tr key={index}>
                  <td>
                    <Button
                      id={`historico_${index}`}
                      fill
                      color="font"
                      round
                      onClick={() =>
                        selectedHistorico === insercao.id
                          ? setSelectedHistorico(0)
                          : handleHistoricoVinculacao(insercao)
                      }
                    >
                      {selectedHistorico === insercao.id ? (
                        <FiChevronDown />
                      ) : (
                        <FiChevronRight />
                      )}
                    </Button>
                  </td>
                  <td>
                    <Column disabled={insercao.duracao === 0}>
                      <strong>
                        {isBloco ? insercao.turno : `BLOCO ${insercao.bloco}`}
                      </strong>
                      <span>SEQ {insercao.sequencia}</span>
                    </Column>
                  </td>
                  <td>
                    <Column disabled={insercao.duracao === 0}>
                      <strong>{insercao.cliente.mnemonico}</strong>
                      <span>{insercao.cargo}</span>
                    </Column>
                  </td>
                  <td>
                    <strong
                      style={{
                        color:
                          insercao.duracao === 60
                            ? themeContext?.attention
                            : themeContext?.table.fontColor,
                      }}
                    >
                      {insercao.duracao > 60
                        ? `${Math.floor(
                            insercao.duracao / 60,
                          )}m${insercao.duracao % 60}s`
                        : `${insercao.duracao}seg`}
                    </strong>
                  </td>
                  {isBloco && (
                    <td>
                      <Column disabled={insercao.duracao === 0}>
                        <div className="horario">
                          <p>INÍCIO:</p>
                          <p> {insercao.inicio}</p>
                        </div>
                        <div className="horario">
                          <p>FIM:</p>
                          <p> {insercao.fim}</p>
                        </div>
                      </Column>
                    </td>
                  )}
                  <td>
                    <Column disabled={insercao.duracao === 0}>
                      <span className="mapa">
                        <p>MAPA:</p>
                        {format(
                          parseISO(insercao.deadlinemapa),
                          'dd/MM/yyyy HH:mm',
                        )}
                      </span>
                      <span className="mapa">
                        <p> MATERIAL:</p>
                        {format(
                          parseISO(insercao.deadlinematerial),
                          'dd/MM/yyyy HH:mm',
                        )}
                      </span>
                    </Column>
                  </td>
                  <td>
                    <Row>
                      <DropDownBubble
                        style={{ marginRight: '10px' }}
                        options={options}
                        disabled={
                          !insercao.mapademidia || insercao.duracao === 0
                        }
                        selected={options.find(
                          op => op.value === insercao.statusmapa,
                        )}
                        unclickable={isBloco}
                        onChange={value =>
                          handleUpdateStatusMapaMidia(insercao, value.value)
                        }
                      />
                      <Column
                        disabled={
                          !!(
                            insercao.mapademidia &&
                            insercao.mapademidia.disabled_at
                          )
                        }
                      >
                        {insercao.mapademidia && (
                          <>
                            <a
                              style={{ display: 'table-cell' }}
                              target="_blank"
                              rel="noopener noreferrer"
                              id={`id_mapa_${insercao.id}`}
                              href={insercao.mapademidia.download_url}
                            >
                              {insercao.mapademidia.originalfilename}
                            </a>

                            <Tooltip target={`id_mapa_${insercao.id}`}>
                              {insercao.mapademidia.originalfilename}
                            </Tooltip>
                            <span>
                              {insercao.mapademidia.recebido_at
                                ? format(
                                    parseISO(
                                      insercao.mapademidia.recebido_at.toString(),
                                    ),
                                    'dd/MM/yyyy HH:mm',
                                  )
                                : ''}
                            </span>
                          </>
                        )}
                      </Column>
                    </Row>
                  </td>
                  <td>
                    <Row>
                      <DropDownBubble
                        style={{ marginRight: '10px' }}
                        selected={
                          // TO DO: VER ISSO!!!!!!!!
                          options.find(op => op.value === insercao.status) ||
                          options[0]
                        }
                        disabled={
                          (!isBloco && !insercao.comercial) ||
                          (isBloco &&
                            !insercao.mapademidia &&
                            insercao.statusmaterial !== 'slide') ||
                          insercao.duracao === 0
                        }
                        unclickable={isBloco}
                        options={options}
                        onChange={value =>
                          handleUpdateStatusMaterial(insercao, value.value)
                        }
                      />
                      {isBloco ? (
                        insercao.statusmaterial === 'slide' ? (
                          <Column>
                            <strong>SLIDE</strong>
                          </Column>
                        ) : (
                          insercao.mapademidia && (
                            <Column>
                              <strong>
                                {insercao.mapademidia.cmbloco
                                  ? `BL${insercao.mapademidia.cmbloco
                                      .toString()
                                      .padStart(3, '0')}`
                                  : ''}
                                {'\u00A0-\u00A0'}
                                {insercao.mapademidia.titulo}
                              </strong>
                            </Column>
                          )
                        )
                      ) : !insercao.substituir && insercao.comercial ? (
                        <Column $vetado={!!insercao.comercial.veto}>
                          <strong
                            id={`veto_${insercao.id}`}
                            className="codMaterial"
                            onClick={() =>
                              copyToClipboard(insercao.comercial?.codmaterial)
                            }
                          >
                            {insercao.comercial.codmaterial}
                            {'\u00A0-\u00A0'}
                            {insercao.comercial.titulo}
                          </strong>

                          <span>{formatRecebidoAt(insercao.comercial)}</span>
                          {insercao?.comercial?.veto?.observacao && (
                            <Tooltip
                              target={`veto_${insercao.id}`}
                              position="left"
                            >
                              <p>{insercao?.comercial?.veto?.observacao}</p>
                            </Tooltip>
                          )}
                        </Column>
                      ) : (
                        <Column>
                          <AsyncSelect
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            searchable
                            type="default"
                            disabled={
                              insercao.duracao === 0 || insercao.disableSelect
                            }
                            loadOptions={(value: string) =>
                              !insercao.disableSelect &&
                              loadComerciais(value, insercao)
                            }
                            defaultOptions
                            placeholder="Selecione o material"
                            isClearable
                            onChange={(selectedMaterial: ComercialOption) =>
                              handleSelectMaterial(selectedMaterial, insercao)
                            }
                            isMulti={false}
                          />
                        </Column>
                      )}
                    </Row>
                  </td>
                  {isBloco && (
                    <td>
                      <Button
                        id={`obs_${insercao.id}`}
                        fill
                        color="font"
                        round
                        onClick={() =>
                          handleObservacao(
                            insercao.mapademidia &&
                              insercao.mapademidia.observacao
                              ? insercao.mapademidia.observacao
                              : 'Nenhuma observação apontada',
                          )
                        }
                        disabled={
                          !insercao.mapademidia ||
                          !insercao.mapademidia.observacao
                        }
                      >
                        <FiMessageSquare />
                      </Button>
                    </td>
                  )}
                  <td>
                    <DropDownMenu
                      isDropUp={
                        index > insercoes.length - 5 && insercoes.length > 7
                      }
                      id={`acoes${index}`}
                      disabled={false}
                      actions={
                        isBloco
                          ? [
                              {
                                value: 5,
                                label: 'SLIDE',
                              },
                            ]
                          : [
                              {
                                value: 1,
                                label: 'DESVINCULAR',
                                disabled: !insercao.comercial,
                              },
                              {
                                value: 2,
                                label: 'TORNAR 0seg',
                                disabled:
                                  isBloco ||
                                  insercao.duracao === 0 ||
                                  insercao.duracao === 60,
                              },
                              {
                                value: 3,
                                label: 'TORNAR 30seg',
                                disabled: isBloco || insercao.duracao === 30,
                              },
                              {
                                value: 4,
                                label: 'TORNAR 60seg',
                                disabled:
                                  isBloco ||
                                  insercao.duracao === 60 ||
                                  insercao.duracao === 0,
                              },
                            ]
                      }
                      handleClick={(action: { value: number; label: string }) =>
                        handleActionSelect(action.value, insercao)
                      }
                    />
                  </td>
                </tr>
                {selectedHistorico === insercao.id ? (
                  <tr>
                    <td colSpan={isBloco ? 10 : 8} style={{ padding: 0 }}>
                      {!loadingHistorico ? (
                        !historico || historico.length === 0 ? (
                          <p style={{ margin: '4px' }}>
                            Inserção sem historico
                          </p>
                        ) : (
                          <table>
                            <thead>
                              <tr>
                                <th style={{ width: '155px' }}>DATA / HORA</th>
                                <th>TITULO</th>
                                <th style={{ width: '75px' }}>MAPA</th>
                                <th style={{ width: '75px' }}>STATUS</th>
                                <th>EVENTO</th>
                                <th>USUARIO</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historico.map((h: HistoricoItem) => (
                                <tr key={`historico_${h._id}`}>
                                  <td>
                                    {format(
                                      new Date(h.createdAt),
                                      'dd/MM/yyyy - HH:mm',
                                    )}
                                  </td>
                                  <td>
                                    {h.comercial ? h.comercial.titulo : '-'}
                                  </td>
                                  <td>
                                    <DropDownBubble
                                      style={{ justifyContent: 'center' }}
                                      options={[]}
                                      unclickable
                                      selected={options.find(
                                        op => op.value === h.statusmapa,
                                      )}
                                    />
                                  </td>
                                  <td>
                                    <DropDownBubble
                                      style={{ justifyContent: 'center' }}
                                      options={[]}
                                      unclickable
                                      selected={options.find(
                                        op => op.value === h.status,
                                      )}
                                    />
                                  </td>
                                  <td>{h.observacao}</td>
                                  <td>{h.usuario.nome}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      ) : (
                        <p style={{ margin: '4px' }}>Carregando...</p>
                      )}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
        </tbody>
      </table>
    );
  }, [
    copyToClipboard,
    formatRecebidoAt,
    handleActionSelect,
    handleHistoricoVinculacao,
    handleObservacao,
    handleSelectMaterial,
    handleUpdateStatusMapaMidia,
    handleUpdateStatusMaterial,
    historico,
    insercoes,
    isBloco,
    loadComerciais,
    loadingHistorico,
    selectedHistorico,
    themeContext?.attention,
    themeContext?.table.fontColor,
  ]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'duracao') {
        setInsercoes(prevState =>
          prevState.map(i => {
            return i.id === selected.id ? { ...i, duracao: 0 } : i;
          }),
        );
      }
      if (callbackDialog.success.action === 'vinculacao') {
        // Fecha subitens (histórico de inserção)
        setSelectedHistorico(0);
        setInsercoes(prevState =>
          prevState.map(i => {
            return i.id === selected.id ? callbackDialog.success.data : i;
          }),
        );
      }
    }
  }, [callbackDialog, selected.id]);

  useEffect(() => {
    const options = {
      type: 'warning',
      contentHTML: `<h4>ATENÇÃO<h4><h1>PRAÇA SELECIONADA: ${emissora.label}</h1>`,
      allowOutsideClick: false,
      cancelButtonText: 'OK',
      showConfirmButton: false,
    };
    dispatch(show(options));
  }, [dispatch, emissora.label]);

  useEffect(() => {
    loadInsercoes();
  }, [currentDate, loadInsercoes]);

  return (
    <Container>
      {!loadingTable && (
        <>
          <PerfectScrollbar className="scroll">
            {renderTableInsercao()}
          </PerfectScrollbar>
        </>
      )}
    </Container>
  );
}
